import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { Login } from './components/auth/Login';
import { PermisosProvider } from './context/PermisosContext';
import { ObrasProvider } from './context/ObrasContext';
import { TareasProvider } from './context/TareasContext';
import { Home } from './pages/Home';
import { Obras } from './pages/Obras';
import Usuarios from './pages/Usuarios';
import DocumentosPage from './pages/Documentos';
import { Materiales } from './pages/Materiales';
import Tareas from './pages/Tareas';
import { Herramientas } from './pages/Herramientas';
import Configuracion from './pages/Configuracion';
import PresupuestoPage from './pages/PresupuestoPage';
import { AIAssistantWidget } from './components/ai/AIAssistantWidget';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { es } from 'date-fns/locale';
import { useAuth } from './context/AuthContext';

// Lazy load FotosPage
const FotosPage = lazy(() => import('./pages/Fotos'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { usuario, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!usuario) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <>
      <CssBaseline />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <PermisosProvider>
            <ObrasProvider>
              <TareasProvider>
                <Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                  </Box>
                }>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Home />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/obras"
                      element={
                        <ProtectedRoute>
                          <Obras />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usuarios"
                      element={
                        <ProtectedRoute>
                          <Usuarios />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/fotos"
                      element={
                        <ProtectedRoute>
                          <FotosPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/documentos"
                      element={
                        <ProtectedRoute>
                          <DocumentosPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/materiales"
                      element={
                        <ProtectedRoute>
                          <Materiales />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/presupuesto"
                      element={
                        <ProtectedRoute>
                          <PresupuestoPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tareas"
                      element={
                        <ProtectedRoute>
                          <Tareas />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/herramientas"
                      element={
                        <ProtectedRoute>
                          <Herramientas />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/configuracion"
                      element={
                        <ProtectedRoute>
                          <Configuracion />
                        </ProtectedRoute>
                      }
                    />

                  </Routes>
                </Suspense>
                <AIAssistantWidget />
              </TareasProvider>
            </ObrasProvider>
          </PermisosProvider>
        </LocalizationProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
